<template lang="pug">
    .voice-call-container(v-if="voiceCallState !== 'idle' && featureFlags.INBOUND_CALL")
      aiq-voice-call-controller(:mode="voiceCallState"
                                :onCall="startTimer"
                                :metaData="metadata"
                                @ui-event="onVoiceCallEvent")
</template>

<script>
import { mapState } from 'vuex';
import * as log from 'loglevel';
import { messages } from '@agentiq/aiq-js-libs';
import pick from 'lodash/pick';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { AGENT_STATUS, FIXED_AGENT_STATUS, CALL_TYPE } from '@/constants';

const paused = get(FIXED_AGENT_STATUS, 'paused.value');
const onPhone = get(FIXED_AGENT_STATUS, 'paused.subStatus.onPhone');
const realtimeStates = [onPhone];


let voiceClient = null;
const CLIENT_STATE = {
  incoming: 'incoming',
  disconnected: 'disconnected',
  accepted: 'accepted',
};

const VOICE_CALL_STATE = {
  IDLE: 'idle',
  INCOMING: 'incoming',
  IN_CALL: 'in_call',
};

export default {
  name: 'IncomingCall',
  async mounted() {
    try {
      //do not init new client on route change for ongoing calls
      if (voiceClient === null) {
        log.info('Initializing voice client');
        const voiceToken = await this.$store.dispatch('agent/getVoiceToken',
          { identity: this.agentVoiceIdentity });
        voiceClient = new this.$aiq.VoiceClient(voiceToken);
        this.initVoiceClient(voiceClient);
      }
    } catch (err) {
      this.$aiq.notify.error('Unable to initiate voice client');
    }
  },
  computed: {
    ...mapState({
      agent: state => state.agent.profile,
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
      voiceCallState: state => state.inbound.voiceCallState,
      caller: state => state.inbound.caller,
      sid: state => state.inbound.sid,
      startTimer: state => state.inbound.startTimer,
      customer: state => state.inbound.customer,
      conversation: state => state.inbound.conversation,
      metadata: state => state.inbound.metadata,
    }),
    agentVoiceIdentity() {
      return `agent-${this.agent.id}`;
    },
  },
  methods: {
    /**
     * Initialize the voice client to respond to different events
     * @param {Object} client - the voice client initialized
     */
    initVoiceClient(client) {
      client.on(CLIENT_STATE.incoming, async(event) => {
        log.info('Incoming call event');
        await this.$store.dispatch('inbound/updateCaller', event.parameters.From);
        //since we use a call redirect, which updates the callsid
        //we use the original callsid for the namespace which holds the
        //recording and other information
        await this.$store.dispatch('inbound/updateCallSid', event.customParameters.get('sid'));
        //fetch customer and conversation
        //before we proceed to accept or decline the call
        await this.fetchConversation(event.parameters.From);
        await this.updateCallMetadata();
        await this.$store.dispatch('inbound/updateCallState', VOICE_CALL_STATE.INCOMING);
      });
      client.on(CLIENT_STATE.disconnected, async() => {
        log.info('Call disconnected');
        await this.$store.dispatch('conversations/updateVoiceCallContext', false);
        await this.$store.dispatch('inbound/updateCallState', VOICE_CALL_STATE.IDLE);
        await this.$store.dispatch('inbound/updateTimerState', false);
        await this.$store.dispatch('conversations/endCall', { id: this.conversation.id });
        this.updateSubstatus(false);

      });
      client.on(CLIENT_STATE.accepted, async() => {
        log.info('Call accepted');
        await this.$store.dispatch('inbound/updateCallState', VOICE_CALL_STATE.IN_CALL);
      });
    },

    async updateCallMetadata() {
      const currentCustomer = this.customer.data;
      const currentConv = this.conversation;

      return await this.$store.dispatch('inbound/updateMetadata', {
        inbound: true,
        sms: this.caller,
        name: `${currentCustomer.profile.first_name} ${currentCustomer.profile.last_name}`,
        location: get(currentConv, 'queue_options.callMetaData.location', ''),
        kind: get(currentConv, 'queue_options.callMetaData.type', ''),
      });
    },

    /**
     * Fetches customer and conversation for an incoming call
     * @param {string} caller - the caller's phone number
     */
    async fetchConversation(caller) {
      try {
        const customer = await this.$store.dispatch('conversations/fetchCustomerByPhone', caller);
        await this.$store.dispatch('inbound/updateCustomer', customer);
        const conversation = await this.$store.dispatch('conversations/getConversationIdFromCustomer', { customerId: customer.data.id });
        await this.$store.dispatch('inbound/updateConversation', conversation);
      } catch (err) {
        this.$aiq.notify.error('Unable to fetch customer and conversation');
        log.error(err.message);
      }
    },

    async handleFeatureMessage(feature, state, msg) {
      return await this.sendSystemMessage([feature, state].join('.'), msg);
    },

    async sendSystemMessage(action, content) {
      return await this.$store.dispatch('messages/sendSystemMessage', {
        conversation: pick(this.conversation, ['id']),
        action,
        content,
      });
    },
    /**
     * Mimics customer sending a message to initiate a voice call
     */
    async sendMessage() {
      await this.handleFeatureMessage('phone-call', 'agent.pick-up', 'Phone pick-up button is clicked by agent.');
      const payload = assign({ id: this.conversation.id }, messages.makeCallMessage(
        'Phone incoming',
        this.sid,
        'phone',
      ));
      payload.call['receiver'] = this.agent.id;
      payload.call['subType'] = CALL_TYPE.INBOUND;

      try {
        const customer_identities = get(this.customer.data, 'identities');
        delete customer_identities.sms;
        //mimic customer sending mesage since this is an inbound call
        await this.$store.dispatch('conversations/sendCustomerMessage', {
          conversation: { ...this.conversation, channel: 'webchat' },
          customer: { identities: customer_identities },
          message: payload,
        });
      } catch (err) {
        this.$aiq.notify.error('Unable to send a message');
      }
    },

    /**
     * Update the agent status to either paused with a substatus of onPhone
     * or to the previous status if the agent was in a call
     * @param {boolean} inCall - whether the agent is in a call or not
     */
    async updateSubstatus(inCall) {
      let newStatus = {
        value: paused,
        status: '',
      };
      if (inCall) {
        newStatus.status = onPhone;
      }

      if (isEmpty(newStatus.status)) {
        // if nothing is set, we need to go back to previous state.
        newStatus = await this.getPreviousAgentStatus();
      }

      try {
        await this.$store.dispatch('agent/changeAgentStatus', newStatus);
      } catch (err) {
        this.$aiq.notify.error(this.$t('conversation_tab.chat_panel.status_update_error_msg'));
      }
    },

    async getPreviousAgentStatus() {
      try {
        const statusHistory = await this.$store.dispatch('agent/getAgentStatusHistory');
        const previousStatus = statusHistory.find(s => !realtimeStates.includes(s.status));
        return previousStatus;
      } catch (err) {
        log.warn(err.message);
      }
      return { value: AGENT_STATUS.Available.field, status: '' }; // move to available if there is an error.
    },

    /**
     * Navigates to the conversation page for the specified customer.
     * @param {string|number} customerId - The ID of the customer whose conversation page should be opened.
     */

    async pushToConversation(customerId) {
      try {
        await this.$router.push({
          name: 'ConversationByCustomerId',
          params: { id: customerId },
        });
      } catch (err) {
        this.$aiq.notify.error(`Unable to route to the customer ${customerId}`);
      }
    },
    async onVoiceCallEvent({ name, value }) {
      if (!voiceClient) {
        return log.warn('Voice client instance not available');
      }

      log.info(`Voice call event: ${name}`);
      if (name === 'hang-up') {
        if (this.voiceCallState === VOICE_CALL_STATE.INCOMING) {
          await Promise.all([
            // hit endpoint that re-route call to a new agent
            this.$store.dispatch('conversations/rejectCall', { id: this.conversation.id }),
            this.$store.dispatch('inbound/updateCallState', VOICE_CALL_STATE.IDLE),
            this.handleFeatureMessage('phone-call', 'agent.reject', 'Phone reject button is clicked by agent.'),
          ]);
          this.updateSubstatus(false);
        } else {
          voiceClient.hangup();
          await this.handleFeatureMessage('phone-call', 'agent.hang-up', 'Phone hang-up button is clicked by agent.');
        }

      } else if (name === 'mute') {
        voiceClient.mute(value);
      } else if (name === 'pick-up') {
        await this.$store.dispatch('inbound/updateCallState', VOICE_CALL_STATE.IN_CALL);
        voiceClient.accept();
        await this.pushToConversation(this.customer.data.id);
        await this.$store.dispatch('inbound/updateTimerState', true);
        await this.sendMessage();
        await this.updateSubstatus(true);
        this.$store.dispatch('conversations/updateVoiceCallContext', true);
      }
    },
  },
};
</script>

<style>
.voice-call-container {
  position: absolute;
  z-index: 999999;
  left: 20px;
  top: 45px;
}
</style>
